import { loadJson, saveJson } from '@/lib/storage';

/**
 * Device preferences (V1-06) — plain persisted booleans, JSON-encoded via
 * src/lib/storage (values are literally `true`/`false` in localStorage).
 *
 * KEYS ARE A CROSS-PACKET CONTRACT (flagged in docs/report/v1-06.md):
 * - 'sg.sound'  — new-order chime on/off. Default ON. V1-05's realtime chime
 *   must read `isSoundEnabled()` at play time (no event bus needed).
 * - 'sg.print'  — "open the browser print dialog automatically after a
 *   completed sale". Default ON. V1-04's receipt screen reads
 *   `isAutoPrintEnabled()` before calling window.print(); the manual Print
 *   button works regardless. Browser print only — no printer drivers in V1.
 */

export const SOUND_PREF_KEY = 'sg.sound';
export const PRINT_PREF_KEY = 'sg.print';
export const SALE_COMPLETION_PREF_KEY = 'sg.retail-sale-completion';
export const RECEIPT_BRANDING_PREF_KEY = 'sg.receipt-branding';
export const RECEIPT_HEADER_PREF_KEY = 'sg.receipt-header';
export const RECEIPT_ADDRESS_PREF_KEY = 'sg.receipt-address';
export const RECEIPT_PHONE_PREF_KEY = 'sg.receipt-phone';
export const RECEIPT_FOOTER_PREF_KEY = 'sg.receipt-footer';
export const RECEIPT_LOGO_PREF_KEY = 'sg.receipt-logo.v1';
export const INVOICE_ORIENTATION_PREF_KEY = 'sg.invoice-orientation';
const RECEIPT_BLANK_DEFAULTS_MIGRATION_KEY = 'sg.receipt-blank-defaults.v1';

/**
 * Bounds match the currently deployed Windows companion contract. Its spare
 * centred header line accepts 30 characters and its native receipt footer
 * accepts 160. Keeping the limits here means browser and direct thermal output
 * cannot silently disagree through bridge-side truncation.
 */
export const RECEIPT_HEADER_MAX_LENGTH = 30;
export const RECEIPT_ADDRESS_MAX_LENGTH = 160;
export const RECEIPT_PHONE_MAX_LENGTH = 40;
export const RECEIPT_FOOTER_MAX_LENGTH = 160;
export const DEFAULT_RECEIPT_FOOTER = '';
export const RECEIPT_LOGO_MAX_FILE_BYTES = 2 * 1024 * 1024;
export const RECEIPT_LOGO_MAX_WIDTH_DOTS = 384;
export const RECEIPT_LOGO_MAX_HEIGHT_DOTS = 240;
export type InvoiceOrientation = 'portrait' | 'landscape';

export interface ReceiptLogoPreference {
  /** Version 2 artwork is already prepared at the current thermal size. */
  rasterVersion?: 2;
  previewDataUrl: string;
  width: number;
  height: number;
  /** Row-major 1-bit raster bytes, base64 encoded for the loopback JSON wire. */
  data: string;
}

function normalizeReceiptText(value: string, maxLength: number): string {
  return value.replace(/\s+/g, ' ').trim().slice(0, maxLength);
}

function loadReceiptText(key: string, fallback: string, maxLength: number): string {
  const stored = loadJson<unknown>(key);
  return normalizeReceiptText(typeof stored === 'string' ? stored : fallback, maxLength);
}

/**
 * One early local prototype seeded these two demo strings. They were persisted,
 * so changing the source defaults to blank did not remove them from an existing
 * till. Clear only the exact demo pair once; any real merchant customization is
 * preserved, including either phrase when it was saved independently.
 */
function migrateLegacyReceiptDemoText(): void {
  if (loadJson<boolean>(RECEIPT_BLANK_DEFAULTS_MIGRATION_KEY)) return;
  const header = loadJson<unknown>(RECEIPT_HEADER_PREF_KEY);
  const footer = loadJson<unknown>(RECEIPT_FOOTER_PREF_KEY);
  if (header === 'Hello Bhai' && footer === 'Thank you!') {
    saveJson(RECEIPT_HEADER_PREF_KEY, '');
    saveJson(RECEIPT_FOOTER_PREF_KEY, '');
  }
  saveJson(RECEIPT_BLANK_DEFAULTS_MIGRATION_KEY, true);
}

export function isSoundEnabled(): boolean {
  return loadJson<boolean>(SOUND_PREF_KEY) ?? true;
}

export function setSoundEnabled(enabled: boolean): void {
  saveJson(SOUND_PREF_KEY, enabled);
}

export function isAutoPrintEnabled(): boolean {
  return loadJson<boolean>(PRINT_PREF_KEY) ?? true;
}

export function setAutoPrintEnabled(enabled: boolean): void {
  saveJson(PRINT_PREF_KEY, enabled);
}

/** Optional ecommerce receipt dialog; off by default to keep the next sale ready. */
export function isSaleCompletionEnabled(): boolean {
  return loadJson<unknown>(SALE_COMPLETION_PREF_KEY) === true;
}

export function setSaleCompletionEnabled(enabled: boolean): void {
  saveJson(SALE_COMPLETION_PREF_KEY, enabled);
}

export function isReceiptBrandingEnabled(): boolean {
  return loadJson<boolean>(RECEIPT_BRANDING_PREF_KEY) ?? true;
}

export function setReceiptBrandingEnabled(enabled: boolean): void {
  saveJson(RECEIPT_BRANDING_PREF_KEY, enabled);
}

export function getReceiptHeaderText(): string {
  migrateLegacyReceiptDemoText();
  return loadReceiptText(RECEIPT_HEADER_PREF_KEY, '', RECEIPT_HEADER_MAX_LENGTH);
}

export function setReceiptHeaderText(value: string): boolean {
  const normalized = normalizeReceiptText(value, RECEIPT_HEADER_MAX_LENGTH);
  saveJson(RECEIPT_HEADER_PREF_KEY, normalized);
  return getReceiptHeaderText() === normalized;
}

export function getReceiptAddressText(): string {
  return loadReceiptText(RECEIPT_ADDRESS_PREF_KEY, '', RECEIPT_ADDRESS_MAX_LENGTH);
}

export function setReceiptAddressText(value: string): boolean {
  const normalized = normalizeReceiptText(value, RECEIPT_ADDRESS_MAX_LENGTH);
  saveJson(RECEIPT_ADDRESS_PREF_KEY, normalized);
  return getReceiptAddressText() === normalized;
}

export function getReceiptPhoneText(): string {
  return loadReceiptText(RECEIPT_PHONE_PREF_KEY, '', RECEIPT_PHONE_MAX_LENGTH);
}

export function setReceiptPhoneText(value: string): boolean {
  const normalized = normalizeReceiptText(value, RECEIPT_PHONE_MAX_LENGTH);
  saveJson(RECEIPT_PHONE_PREF_KEY, normalized);
  return getReceiptPhoneText() === normalized;
}

export function getReceiptFooterText(): string {
  migrateLegacyReceiptDemoText();
  return loadReceiptText(
    RECEIPT_FOOTER_PREF_KEY,
    DEFAULT_RECEIPT_FOOTER,
    RECEIPT_FOOTER_MAX_LENGTH,
  );
}

export function setReceiptFooterText(value: string): boolean {
  const normalized = normalizeReceiptText(value, RECEIPT_FOOTER_MAX_LENGTH);
  saveJson(RECEIPT_FOOTER_PREF_KEY, normalized);
  return getReceiptFooterText() === normalized;
}

export function getInvoiceOrientation(): InvoiceOrientation {
  const stored = loadJson<unknown>(INVOICE_ORIENTATION_PREF_KEY);
  return stored === 'landscape' ? 'landscape' : 'portrait';
}

export function setInvoiceOrientation(value: InvoiceOrientation): boolean {
  saveJson(INVOICE_ORIENTATION_PREF_KEY, value);
  return getInvoiceOrientation() === value;
}

export function getReceiptLogo(): ReceiptLogoPreference | null {
  const value = loadJson<unknown>(RECEIPT_LOGO_PREF_KEY);
  if (!value || typeof value !== 'object') return null;
  const candidate = value as Partial<ReceiptLogoPreference>;
  const width = candidate.width;
  const height = candidate.height;
  if (
    typeof candidate.previewDataUrl !== 'string' ||
    !candidate.previewDataUrl.startsWith('data:image/png;base64,') ||
    typeof candidate.data !== 'string' ||
    typeof width !== 'number' ||
    typeof height !== 'number' ||
    !Number.isInteger(width) ||
    !Number.isInteger(height) ||
    width < 1 ||
    width > RECEIPT_LOGO_MAX_WIDTH_DOTS ||
    height < 1 ||
    height > RECEIPT_LOGO_MAX_HEIGHT_DOTS
  ) {
    return null;
  }
  const expectedBytes = Math.ceil(width / 8) * height;
  try {
    const binary = atob(candidate.data);
    if (binary.length !== expectedBytes) return null;

    /*
     * Repair the old saved thermal bitmap at read time.
     *
     * Earlier builds thresholded every dark pixel as a printer dot. A logo
     * with a black full-bleed background was therefore saved as a black box
     * with its bright mark punched out. Fixing prepareReceiptLogo only helped
     * after the merchant uploaded the file again; the already-saved bitmap
     * kept printing incorrectly. Detect the same mostly-black perimeter as
     * the Windows bridge and invert only real image pixels. Padding bits at
     * the end of each byte row must remain zero. This also means the corrected
     * data works with the currently installed companion, without rebuilding it.
     */
    let raster = Uint8Array.from(binary, (character) => character.charCodeAt(0));
    let rasterWidth = width;
    let rasterHeight = height;
    const rowBytes = Math.ceil(width / 8);
    const isBlack = (x: number, y: number) =>
      ((raster[y * rowBytes + Math.floor(x / 8)] ?? 0) & (0x80 >> (x % 8))) !== 0;
    let borderDots = 0;
    let borderPixels = 0;
    for (let x = 0; x < width; x += 1) {
      if (isBlack(x, 0)) borderDots += 1;
      if (height > 1 && isBlack(x, height - 1)) borderDots += 1;
      borderPixels += height > 1 ? 2 : 1;
    }
    for (let y = 1; y < height - 1; y += 1) {
      if (isBlack(0, y)) borderDots += 1;
      if (width > 1 && isBlack(width - 1, y)) borderDots += 1;
      borderPixels += width > 1 ? 2 : 1;
    }
    let repaired = false;
    if (candidate.rasterVersion !== 2 && borderPixels > 0 && borderDots / borderPixels > 0.6) {
      for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
          const byteIndex = y * rowBytes + Math.floor(x / 8);
          const mask = 0x80 >> (x % 8);
          raster[byteIndex] = isBlack(x, y)
            ? (raster[byteIndex] ?? 0) & ~mask
            : (raster[byteIndex] ?? 0) | mask;
        }
      }
      repaired = true;
    }

    // Previous uploads were capped at 240x120, which made a square brand mark
    // print at only 120 dots (about 15 mm). Upscale that existing 1-bit image
    // with nearest-neighbour dots so the installed preference also uses the
    // expanded receipt-logo area. New uploads are rasterized directly from the
    // original image and do not take this compatibility path.
    const scale =
      candidate.rasterVersion === 2
        ? 1
        : Math.min(
            2,
            RECEIPT_LOGO_MAX_WIDTH_DOTS / rasterWidth,
            RECEIPT_LOGO_MAX_HEIGHT_DOTS / rasterHeight,
          );
    if (scale > 1) {
      const source = raster;
      const sourceWidth = rasterWidth;
      const sourceHeight = rasterHeight;
      const sourceRowBytes = Math.ceil(sourceWidth / 8);
      rasterWidth = Math.min(RECEIPT_LOGO_MAX_WIDTH_DOTS, Math.round(sourceWidth * scale));
      rasterHeight = Math.min(RECEIPT_LOGO_MAX_HEIGHT_DOTS, Math.round(sourceHeight * scale));
      const outputRowBytes = Math.ceil(rasterWidth / 8);
      raster = new Uint8Array(outputRowBytes * rasterHeight);
      for (let y = 0; y < rasterHeight; y += 1) {
        const sourceY = Math.min(sourceHeight - 1, Math.floor(y / scale));
        for (let x = 0; x < rasterWidth; x += 1) {
          const sourceX = Math.min(sourceWidth - 1, Math.floor(x / scale));
          const sourceByte = source[sourceY * sourceRowBytes + Math.floor(sourceX / 8)] ?? 0;
          if ((sourceByte & (0x80 >> (sourceX % 8))) !== 0) {
            const outputIndex = y * outputRowBytes + Math.floor(x / 8);
            raster[outputIndex] = (raster[outputIndex] ?? 0) | (0x80 >> (x % 8));
          }
        }
      }
      repaired = true;
    }

    if (repaired) {
      return {
        ...(candidate as ReceiptLogoPreference),
        rasterVersion: 2,
        width: rasterWidth,
        height: rasterHeight,
        data: bytesToBase64(raster),
      };
    }
  } catch {
    return null;
  }
  return candidate as ReceiptLogoPreference;
}

export function setReceiptLogo(value: ReceiptLogoPreference | null): boolean {
  saveJson(RECEIPT_LOGO_PREF_KEY, value);
  const stored = getReceiptLogo();
  return value === null
    ? stored === null
    : stored?.data === value.data &&
        stored.width === value.width &&
        stored.height === value.height &&
        stored.previewDataUrl === value.previewDataUrl;
}

function imageFromUrl(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('The selected image could not be read.'));
    image.src = url;
  });
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

/** Resize once on selection; print jobs then carry a small bounded 1-bit logo. */
export async function prepareReceiptLogo(file: File): Promise<ReceiptLogoPreference> {
  if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
    throw new Error('Choose a PNG, JPG or WebP image.');
  }
  if (file.size > RECEIPT_LOGO_MAX_FILE_BYTES) {
    throw new Error('Logo must be 2 MB or smaller.');
  }

  const objectUrl = URL.createObjectURL(file);
  try {
    const image = await imageFromUrl(objectUrl);
    const scale = Math.min(
      1,
      RECEIPT_LOGO_MAX_WIDTH_DOTS / image.naturalWidth,
      RECEIPT_LOGO_MAX_HEIGHT_DOTS / image.naturalHeight,
    );
    const width = Math.max(1, Math.round(image.naturalWidth * scale));
    const height = Math.max(1, Math.round(image.naturalHeight * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) throw new Error('Logo processing is unavailable in this browser.');
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, width, height);
    context.drawImage(image, 0, 0, width, height);

    const pixels = context.getImageData(0, 0, width, height).data;
    const rowBytes = Math.ceil(width / 8);
    const raster = new Uint8Array(rowBytes * height);

    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        const offset = (y * width + x) * 4;
        const alpha = pixels[offset + 3] ?? 0;
        const luminance =
          (pixels[offset] ?? 255) * 0.299 +
          (pixels[offset + 1] ?? 255) * 0.587 +
          (pixels[offset + 2] ?? 255) * 0.114;
        if (alpha >= 128 && luminance < 180) {
          const byteIndex = y * rowBytes + Math.floor(x / 8);
          raster[byteIndex] = (raster[byteIndex] ?? 0) | (0x80 >> (x % 8));
        }
      }
    }

    return {
      rasterVersion: 2,
      previewDataUrl: canvas.toDataURL('image/png'),
      width,
      height,
      data: bytesToBase64(raster),
    };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}
